import { Injectable, signal } from '@angular/core';
import { IdbStorageService, IdbStorage } from './idb-storage.service';
import { ToastService } from './toast.service';
import { game } from '../../game';

const HANDLE_KEY_PREFIX = 'handle:';

@Injectable({ providedIn: 'root' })
export class FileSaveService {
  readonly supportsFileSystemAccess = 'showSaveFilePicker' in window;

  private _knownHandleNames = signal<Set<string>>(new Set());
  readonly knownHandleNames = this._knownHandleNames.asReadonly();

  private fileHandleDb: IdbStorage;

  constructor(private toastService: ToastService, idbStorageService: IdbStorageService) {
    this.fileHandleDb = idbStorageService.createStorage(game + '-file-handles');
    if (this.supportsFileSystemAccess) {
      this.fileHandleDb.idbGetAllKeys().then(keys => {
        const names = keys
          .filter(k => k.startsWith(HANDLE_KEY_PREFIX))
          .map(k => k.slice(HANDLE_KEY_PREFIX.length));
        this._knownHandleNames.set(new Set(names));
      });
    }
  }

  async save(text: string, filterName: string, filename: string): Promise<void> {
    if (this._knownHandleNames().has(filterName)) {
      await this._saveToExistingFile(text, filterName, filename);
    } else {
      await this.saveToNewFile(text, filterName, filename);
    }
  }

  async saveToNewFile(text: string, filterName: string, filename: string): Promise<void> {
    let handle: FileSystemFileHandle;
    try {
      handle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        startIn: 'documents',
        types: [
          {
            description: 'Path of Exile filter',
            accept: { 'text/plain': ['.filter', '.ruthlessfilter'] },
          },
        ],
      });
    } catch (err: any) {
      // User cancelled the picker -- not an error
      if (err?.name === 'AbortError') return;
      this.toastService.raise('error', 'Could not open save dialog.', 'Save failed');
      return;
    }

    await this._writeAndStore(text, filterName, handle);
  }

  private async _saveToExistingFile(text: string, filterName: string, filename: string): Promise<void> {
    const handle = await this.fileHandleDb.idbGet<FileSystemFileHandle>(HANDLE_KEY_PREFIX + filterName);

    if (!handle) {
      this._removeFromKnown(filterName);
      await this.saveToNewFile(text, filterName, filename);
      return;
    }

    let permission: PermissionState;
    try {
      permission = await (handle as any).requestPermission({ mode: 'readwrite' });
    } catch {
      permission = 'denied';
    }

    if (permission !== 'granted') {
      await this.saveToNewFile(text, filterName, filename);
      return;
    }

    await this._writeAndStore(text, filterName, handle);
  }

  private async _writeAndStore(text: string, filterName: string, handle: FileSystemFileHandle): Promise<void> {
    try {
      const writable = await (handle as any).createWritable();
      await writable.write(text);
      await writable.close();
    } catch (err: any) {
      this._removeFromKnown(filterName);
      this.toastService.raise('error', err?.message ?? 'Unknown error while writing file.', 'Save failed');
      return;
    }

    await this.fileHandleDb.idbSet(HANDLE_KEY_PREFIX + filterName, handle);
    const updated = new Set(this._knownHandleNames());
    updated.add(filterName);
    this._knownHandleNames.set(updated);

    this.toastService.raise('success', `Saved to ${handle.name}. Refresh in-game to see the changes.`);
  }

  private _removeFromKnown(filterName: string): void {
    const updated = new Set(this._knownHandleNames());
    updated.delete(filterName);
    this._knownHandleNames.set(updated);
    this.fileHandleDb.idbDelete(HANDLE_KEY_PREFIX + filterName);
  }
}
